// "Where homeowners see you" card of the claim and takedown pages: where a
// profile shows up on and beyond RealAdvisor (each row opens a short
// explanation and a mockup of that placement), what the free profile lets
// you do, and what the paid profile adds.
import { type ReactNode, useState } from 'react';

import {
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Calculator,
  Check,
  ChevronDown,
  type LucideIcon,
  Mail,
  MapPin,
  Search,
  Sparkles,
  Trophy,
} from 'lucide-react';

import { type Translate, useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

import { type CountryShowcase, getCountryShowcase } from './claimShowcaseData';
import {
  AiMock,
  GoogleMock,
  InboxMock,
  PricesMock,
  RankingsMock,
  type ShowcaseEntity,
  ValuationMock,
} from './PlacementMockups';

type PlacementKey =
  | 'valuation'
  | 'rankings'
  | 'prices'
  | 'google'
  | 'ai'
  | 'inbox';

type Placement = {
  key: PlacementKey;
  icon: LucideIcon;
  title: string;
  text: string;
  detail: string;
};

type MockContext = {
  country: CountryShowcase;
  isAgency: boolean;
  you: ShowcaseEntity;
  others: ShowcaseEntity[];
};

const renderMock = (key: PlacementKey, context: MockContext) => {
  switch (key) {
    case 'valuation':
      return <ValuationMock {...context} />;
    case 'rankings':
      return <RankingsMock {...context} />;
    case 'prices':
      return <PricesMock {...context} />;
    case 'google':
      return <GoogleMock {...context} />;
    case 'ai':
      return <AiMock {...context} />;
    case 'inbox':
      return <InboxMock {...context} />;
  }
};

// Uses a function so all strings are literal t() calls — visible to the
// i18n scanner.
const getOnPlacements = (
  t: Translate,
  isAgency: boolean,
  town: string,
): Placement[] => [
  {
    key: 'valuation',
    icon: Calculator,
    title: t('Valuation results'),
    text: isAgency
      ? t('Homeowners who value their home see the agencies active near it.')
      : t('Homeowners who value their home see the agents active near it.'),
    detail: isAgency
      ? t(
          'Homeowners value their home on RealAdvisor every day. Their result recommends the agencies with the most sales near the property, right when they are thinking about selling.',
        )
      : t(
          'Homeowners value their home on RealAdvisor every day. Their result recommends the agents with the most sales near the property, right when they are thinking about selling.',
        ),
  },
  {
    key: 'rankings',
    icon: Trophy,
    title: isAgency ? t('Agency rankings') : t('Agent rankings'),
    text: isAgency
      ? t('The agencies of your town, compared by sales and reviews.')
      : t('The agents of your town, compared by sales and reviews.'),
    detail: isAgency
      ? t(
          'Our pages rank every agency in {{town}} by sales over the last 24 months and by reviews. Homeowners use them to shortlist who to contact.',
          { town },
        )
      : t(
          'Our pages rank every agent in {{town}} by sales over the last 24 months and by reviews. Homeowners use them to shortlist who to contact.',
          { town },
        ),
  },
  {
    key: 'prices',
    icon: MapPin,
    title: t('Price pages'),
    text: t('Local price pages show who sells in the area.'),
    detail: t(
      'Our price pages for every town and neighbourhood are among the most visited pages on RealAdvisor. Next to the prices, they show who sells in the area.',
    ),
  },
];

const getBeyondPlacements = (
  t: Translate,
  isAgency: boolean,
  town: string,
): Placement[] => [
  {
    key: 'google',
    icon: Search,
    title: 'Google',
    text: isAgency
      ? t(
          'Your agency page comes up when homeowners search for an agency in your town.',
        )
      : t(
          'Your profile comes up when homeowners search for an agent in your town.',
        ),
    detail: t(
      'Your RealAdvisor page is one Google shows for searches like “real estate agent {{town}}”. Your reviews appear right in the result.',
      { town },
    ),
  },
  {
    key: 'ai',
    icon: Sparkles,
    title: t('AI assistants'),
    text: t(
      'ChatGPT, Gemini and Claude cite RealAdvisor when homeowners ask for a good local agent.',
    ),
    detail: t(
      'When homeowners ask ChatGPT, Gemini or Claude who to hire, the answers often draw on RealAdvisor’s sales and reviews, and link to your page.',
    ),
  },
  {
    key: 'inbox',
    icon: Mail,
    title: t('In their inbox'),
    text: isAgency
      ? t(
          'Our valuation reports and home-value e-mails recommend local agencies to homeowners.',
        )
      : t(
          'Our valuation reports and home-value e-mails recommend local agents to homeowners.',
        ),
    detail: isAgency
      ? t(
          'After a valuation, homeowners get their report by e-mail, then regular updates on their home’s value. Both recommend the local agencies with the most sales.',
        )
      : t(
          'After a valuation, homeowners get their report by e-mail, then regular updates on their home’s value. Both recommend the local agents with the most sales.',
        ),
  },
];

const IconTile = ({ icon: Icon }: { icon: LucideIcon }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(primary, 0.08),
        border: `1px solid ${alpha(primary, 0.12)}`,
        color: 'primary.main',
      }}
    >
      <Icon size={18} />
    </Box>
  );
};

const PlacementRow = ({
  placement,
  open,
  onToggle,
  mock,
}: {
  placement: Placement;
  open: boolean;
  onToggle: () => void;
  mock: ReactNode;
}) => (
  <Box sx={{ '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        width: 'calc(100% + 16px)',
        mx: -1,
        px: 1,
        py: 1.5,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        textAlign: 'left',
        '&:hover': { bgcolor: customPalette.gray50 },
        '&:hover .placement-chevron': { color: 'primary.main' },
      }}
    >
      <IconTile icon={placement.icon} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}
          >
            {placement.title}
          </Typography>
        </Stack>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: 13,
            lineHeight: 1.5,
            color: 'text.secondary',
          }}
        >
          {placement.text}
        </Typography>
      </Box>
      <Box
        className="placement-chevron"
        sx={{
          alignSelf: 'center',
          display: 'flex',
          color: open ? 'primary.main' : 'text.disabled',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform .2s',
        }}
      >
        <ChevronDown size={16} />
      </Box>
    </ButtonBase>
    <Collapse in={open} unmountOnExit>
      <Box sx={{ pl: { xs: 0, sm: 6.25 }, pb: 2 }}>
        <Typography
          sx={{
            mb: 1.25,
            fontSize: 13,
            lineHeight: 1.55,
            color: 'text.primary',
          }}
        >
          {placement.detail}
        </Typography>
        {mock}
      </Box>
    </Collapse>
  </Box>
);

const Column = ({
  heading,
  hint,
  children,
}: {
  heading: string;
  hint: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: { xs: 2.5, md: 4 },
      pt: { xs: 3, md: 5.5 },
      pb: { xs: 1.5, md: 3 },
    }}
  >
    <Stack
      direction="row"
      spacing={1.25}
      useFlexGap
      sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 1.5 }}
    >
      <Typography
        component="h2"
        sx={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'primary.main',
        }}
      >
        {heading}
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
        {hint}
      </Typography>
    </Stack>
    {children}
  </Box>
);

type Props = {
  isAgency: boolean;
  countryCode: string | null | undefined;
  you: ShowcaseEntity;
};

export const ProfileVisibility = ({ isAgency, countryCode, you }: Props) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const country = getCountryShowcase(countryCode);
  const [openPlacement, setOpenPlacement] = useState<PlacementKey | null>(null);

  const others: ShowcaseEntity[] = isAgency
    ? country.agencies.map(agency => ({ ...agency, square: true }))
    : country.agents.slice(0, 3).map(agent => ({
        name: agent.name,
        photo: agent.photo,
        rating: agent.rating,
        sales: agent.sales,
      }));
  const mockContext: MockContext = { country, isAgency, you, others };

  const renderRows = (placements: Placement[]) =>
    placements.map(placement => (
      <PlacementRow
        key={placement.key}
        placement={placement}
        open={openPlacement === placement.key}
        onToggle={() =>
          setOpenPlacement(current =>
            current === placement.key ? null : placement.key,
          )
        }
        mock={renderMock(placement.key, mockContext)}
      />
    ));

  const freeItems = isAgency
    ? [
        t('Get your team’s leads in the CRM'),
        t('Complete your agency profile'),
        t('Present your team'),
        t('Collect client reviews'),
      ]
    : [
        t('Get your leads in the CRM'),
        t('Complete your profile'),
        t('Collect client reviews'),
      ];

  return (
    <Box
      sx={{
        mt: { xs: 2, md: 0 },
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        divider={
          <Divider
            flexItem
            orientation={isDesktop ? 'vertical' : 'horizontal'}
          />
        }
      >
        <Column
          heading={t('On RealAdvisor')}
          hint={t('where homeowners compare')}
        >
          {renderRows(getOnPlacements(t, isAgency, country.town))}
        </Column>
        <Column
          heading={t('Beyond RealAdvisor')}
          hint={t('where homeowners search')}
        >
          {renderRows(getBeyondPlacements(t, isAgency, country.town))}
        </Column>
      </Stack>

      {/* What the free profile lets you do */}
      <Stack
        direction="row"
        useFlexGap
        sx={{
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 3,
          rowGap: 1,
          px: { xs: 2.5, md: 4 },
          py: 1.75,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: customPalette.gray50,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {t('Free once claimed:')}
        </Typography>
        {freeItems.map(item => (
          <Stack
            key={item}
            direction="row"
            spacing={0.75}
            sx={{ alignItems: 'center', color: 'text.secondary' }}
          >
            <Box sx={{ display: 'flex', color: customPalette.green600 }}>
              <Check size={15} />
            </Box>
            <Typography sx={{ fontSize: 13 }}>{item}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
